import { Rndm } from 'twrk/Rndm.js';
import { Arry } from 'twrk/Arry.js';
import { XY } from 'twrk/V2.js';
import { Shdr, shdr_type } from 'twrk/Shdr.js';
import { Wbgl_mono } from 'twrk/Wbgl_mono.js';
import { wbgl_type } from 'twrk/Wbgl.js';

import { twrk_type } from './types.js';
import { zoom, pan } from './camera.js';

let _shader: shdr_type | null = null;

function get_shader(Canvas: wbgl_type): shdr_type {
    if (!_shader) {
        _shader = Shdr.make(Canvas, {
            id: "lights_2d",
            vertex: `#version 300 es
            precision mediump float;
            in vec2 position;
            uniform vec2 u_screen;
            uniform float u_zoom;
            uniform vec2 u_pan;
            void main() {
                gl_Position = vec4((position - u_pan) * u_zoom * u_screen, 0.0, 1.0);
            }
            `,
            fragment: `#version 300 es
            precision mediump float;
            uniform vec4 u_color;
            out vec4 fragColor;
            void main() {
                fragColor = u_color;
            }
            `
        });
    }
    return _shader;
}

type light_input = {
    id?: string;
    x?: number;
    y?: number;
};

const presets = {
    color: [1, 0.97, 0.8, 0.06], //warm white with alpha, blends over the dark background
    spread: 0.09, //beam half-width gained per world unit of beam length
    overshoot: 6, //world units the beam keeps going past the target
    back: 2, //world units the beam starts behind the lamp, so the apex sits offscreen
    follow: 0.1, //per-frame ease of the aim towards the target, so the beam trails a bit like a human operator
};

export function lights_manager(twrk: twrk_type) {

    const { Canvas, Scrn } = twrk;
    const shader = get_shader(Canvas);

    const list: any[] = [];

    function make(io: light_input = {}) {

        const id = io.id || Rndm.id();
        const x = io.x ?? 0;
        const y = io.y ?? 2;

        //one triangle: apex behind the lamp, base past the target, rebuilt each frame
        const mesh = new Float32Array(6);
        const gl = Wbgl_mono.make(Canvas, shader, Scrn, { mesh, uniforms: { u_color: presets.color, u_zoom: 1, u_pan: [0, 0] } });

        //smoothed aim: eases towards the target instead of locking on, so the beam is always a bit late
        const aim = { x, y };
        let aimed = false;

        const f = {
            id,
            run: function (target: XY) {
                if (!aimed) { aim.x = target.x; aim.y = target.y; aimed = true; }
                aim.x -= (aim.x - target.x) * presets.follow;
                aim.y -= (aim.y - target.y) * presets.follow;
                //lamp rides the camera: x/y are a screen-space offset, so panning and zooming never leave it behind
                const lx = pan.x + x / zoom.value;
                const ly = pan.y + y / zoom.value;
                const dx = aim.x - lx;
                const dy = aim.y - ly;
                const dist = Math.hypot(dx, dy);
                if (!dist) return;
                const len = presets.back + dist + presets.overshoot;
                const dir_x = dx / dist;
                const dir_y = dy / dist;
                const half = len * presets.spread;
                const far_x = lx + dir_x * (dist + presets.overshoot);
                const far_y = ly + dir_y * (dist + presets.overshoot);
                mesh[0] = lx - dir_x * presets.back;
                mesh[1] = ly - dir_y * presets.back;
                mesh[2] = far_x - dir_y * half;
                mesh[3] = far_y + dir_x * half;
                mesh[4] = far_x + dir_y * half;
                mesh[5] = far_y - dir_x * half;
                gl.mesh_update(mesh);
                gl.uniforms.u_zoom = zoom.value;
                gl.uniforms.u_pan = [pan.x, pan.y];
                gl.render();
            },
            kill: function () {
                gl.kill();
                Arry.remove.id(list, id);
            },
        };

        list.push(f);
        return f;
    }

    return {
        list,
        make,
        get: (id: string) => Arry.get.id(list, id),
        kill: (id: string) => { const l = Arry.get.id(list, id); if (l) l.kill(); },
        run: function (target: XY) {
            for (const l of list) l.run(target);
        },
    };
}
